import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Palette, Image, Save, RefreshCw, Eye, Upload, X, Link, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminGetAppearance, adminUpdateAppearance, uploadFile } from '../api/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Image Upload Component
const ImageUploader = ({ label, description, value, onChange, previewHeight = "h-48" }) => {
  const [uploading, setUploading] = useState(false);
  const [useUrl, setUseUrl] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non autorisé. Utilisez JPG, PNG, GIF ou WebP.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux. Taille max: 5MB');
      return;
    }

    setUploading(true);
    try {
      const result = await uploadFile(file);
      // Construct the full URL for the uploaded file
      const fullUrl = `${API_URL}${result.url}`;
      onChange(fullUrl);
      toast.success('Image uploadée avec succès !');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const fakeEvent = { target: { files: [file] } };
      await handleFileSelect(fakeEvent);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label className="font-semibold">{label}</Label>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setUseUrl(!useUrl)}
          className="text-xs"
        >
          <Link className="w-3 h-3 mr-1" />
          {useUrl ? 'Uploader' : 'Utiliser URL'}
        </Button>
      </div>

      {useUrl ? (
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://example.com/image.jpg"
        />
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />

          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-2" />
              <p className="text-sm text-gray-500">Upload en cours...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <Upload className="w-8 h-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 font-medium">
                Cliquez ou glissez-déposez une image
              </p>
              <p className="text-xs text-gray-400 mt-1">
                JPG, PNG, GIF ou WebP (max 5MB)
              </p>
            </div>
          )}
        </div>
      )}

      {value && (
        <div className={`relative ${previewHeight} rounded-lg overflow-hidden bg-gray-100`}>
          <img
            src={value}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden absolute inset-0 items-center justify-center bg-gray-100 text-gray-400">
            Image non trouvée
          </div>
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export const AdminAppearance = () => {
  const [appearance, setAppearance] = useState({
    primary_color: '#2563eb',
    secondary_color: '#16a34a',
    accent_color: '#3b82f6',
    hero_image: '',
    about_image: '',
    logo_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAppearance();
  }, []);

  const fetchAppearance = async () => {
    try {
      const data = await adminGetAppearance();
      setAppearance(data);
    } catch (error) {
      console.error('Error fetching appearance:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminUpdateAppearance(appearance);
      toast.success('Apparence mise à jour avec succès !');
    } catch (error) {
      console.error('Error updating appearance:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setAppearance({
      primary_color: '#2563eb',
      secondary_color: '#16a34a',
      accent_color: '#3b82f6',
      hero_image: 'https://images.pexels.com/photos/6197123/pexels-photo-6197123.jpeg?auto=compress&cs=tinysrgb&w=1920',
      about_image: 'https://images.unsplash.com/photo-1497366858526-0766cadbe8fa?auto=format&fit=crop&w=1000&q=80',
      logo_url: ''
    });
    toast.info('Valeurs par défaut restaurées. Cliquez sur Sauvegarder pour appliquer.');
  };

  const presetColors = [
    { name: 'Bleu Pro', primary: '#2563eb', secondary: '#16a34a', accent: '#3b82f6' },
    { name: 'Vert Nature', primary: '#059669', secondary: '#0891b2', accent: '#10b981' },
    { name: 'Violet Moderne', primary: '#7c3aed', secondary: '#ec4899', accent: '#8b5cf6' },
    { name: 'Orange Energie', primary: '#ea580c', secondary: '#ca8a04', accent: '#f97316' },
    { name: 'Rouge Passion', primary: '#dc2626', secondary: '#991b1b', accent: '#ef4444' },
    { name: 'Bleu Marine', primary: '#1e3a5f', secondary: '#2563eb', accent: '#3b82f6' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Apparence du site</h1>
          <p className="text-gray-500 mt-1">Personnalisez les couleurs et images de votre site</p>
        </div>
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
        >
          <Eye className="w-4 h-4" />
          Voir le site
        </a>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Preset Colors */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-blue-600" />
              Palettes prédéfinies
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {presetColors.map((preset, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setAppearance(prev => ({
                    ...prev,
                    primary_color: preset.primary,
                    secondary_color: preset.secondary,
                    accent_color: preset.accent
                  }))}
                  className="p-4 rounded-lg border-2 hover:border-blue-500 transition-all hover:shadow-lg group"
                  style={{ borderColor: appearance.primary_color === preset.primary ? preset.primary : '#e5e7eb' }}
                >
                  <div className="flex gap-1 mb-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full" style={{ backgroundColor: preset.primary }}></div>
                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full" style={{ backgroundColor: preset.secondary }}></div>
                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full" style={{ backgroundColor: preset.accent }}></div>
                  </div>
                  <p className="text-xs font-medium text-gray-700 group-hover:text-blue-600">{preset.name}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Custom Colors */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-purple-600" />
              Couleurs personnalisées
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Primary Color */}
              <div className="space-y-3">
                <Label htmlFor="primary_color" className="font-semibold">Couleur principale</Label>
                <p className="text-sm text-gray-500">Boutons, liens, éléments importants</p>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    id="primary_color"
                    value={appearance.primary_color}
                    onChange={(e) => setAppearance(prev => ({ ...prev, primary_color: e.target.value }))}
                    className="w-12 h-10 sm:w-16 sm:h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                  />
                  <Input
                    value={appearance.primary_color}
                    onChange={(e) => setAppearance(prev => ({ ...prev, primary_color: e.target.value }))}
                    className="font-mono text-sm"
                    placeholder="#2563eb"
                  />
                </div>
                <div
                  className="h-10 sm:h-12 rounded-lg flex items-center justify-center text-white font-semibold text-sm"
                  style={{ backgroundColor: appearance.primary_color }}
                >
                  Aperçu
                </div>
              </div>

              {/* Secondary Color */}
              <div className="space-y-3">
                <Label htmlFor="secondary_color" className="font-semibold">Couleur secondaire</Label>
                <p className="text-sm text-gray-500">Accents, badges, éléments secondaires</p>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    id="secondary_color"
                    value={appearance.secondary_color}
                    onChange={(e) => setAppearance(prev => ({ ...prev, secondary_color: e.target.value }))}
                    className="w-12 h-10 sm:w-16 sm:h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                  />
                  <Input
                    value={appearance.secondary_color}
                    onChange={(e) => setAppearance(prev => ({ ...prev, secondary_color: e.target.value }))}
                    className="font-mono text-sm"
                    placeholder="#16a34a"
                  />
                </div>
                <div
                  className="h-10 sm:h-12 rounded-lg flex items-center justify-center text-white font-semibold text-sm"
                  style={{ backgroundColor: appearance.secondary_color }}
                >
                  Aperçu
                </div>
              </div>

              {/* Accent Color */}
              <div className="space-y-3">
                <Label htmlFor="accent_color" className="font-semibold">Couleur d'accent</Label>
                <p className="text-sm text-gray-500">Survol, focus, décorations</p>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    id="accent_color"
                    value={appearance.accent_color}
                    onChange={(e) => setAppearance(prev => ({ ...prev, accent_color: e.target.value }))}
                    className="w-12 h-10 sm:w-16 sm:h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                  />
                  <Input
                    value={appearance.accent_color}
                    onChange={(e) => setAppearance(prev => ({ ...prev, accent_color: e.target.value }))}
                    className="font-mono text-sm"
                    placeholder="#3b82f6"
                  />
                </div>
                <div
                  className="h-10 sm:h-12 rounded-lg flex items-center justify-center text-white font-semibold text-sm"
                  style={{ backgroundColor: appearance.accent_color }}
                >
                  Aperçu
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Images */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-green-600" />
              Images du site
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Hero Image */}
            <ImageUploader
              label="Image Hero (Page d'accueil)"
              description="L'image de fond principale en haut du site"
              value={appearance.hero_image}
              onChange={(url) => setAppearance(prev => ({ ...prev, hero_image: url }))}
              previewHeight="h-48"
            />

            {/* About Image */}
            <ImageUploader
              label="Image « À propos »"
              description="L'image de la section « À propos » sur la page d'accueil"
              value={appearance.about_image}
              onChange={(url) => setAppearance(prev => ({ ...prev, about_image: url }))}
              previewHeight="h-48"
            />

            {/* Logo URL */}
            <ImageUploader
              label="Logo personnalisé (optionnel)"
              description="Votre logo. Laissez vide pour utiliser le texte 'NETTO+'"
              value={appearance.logo_url}
              onChange={(url) => setAppearance(prev => ({ ...prev, logo_url: url }))}
              previewHeight="h-24"
            />
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            className="w-full sm:w-auto flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Réinitialiser
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="w-full sm:w-auto flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Sauvegarder les modifications
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};
